// src/components/TopNav/UserMenu.jsx
import { useEffect, useState, Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import {
  ArrowRightOnRectangleIcon,
  Cog8ToothIcon,
  HomeIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { Capacitor } from '@capacitor/core'
import api from '../../services/apiClient'

export default function UserMenu() {
  const [user, setUser] = useState(null)

  // Fetch current user
  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/api/users/session')
        if (!ignore) setUser(res.data)
      } catch {
        if (!ignore) setUser(null)
      }
    })()
    return () => { ignore = true }
  }, [])

  const logout = async () => {
    await api.post('/api/users/logout')
    window.location.href = '/login'
  }

  if (!user) return null

  return (
    <Menu as="div" className="relative ml-3">
      <Menu.Button className="focus:outline-none">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className={clsx(
              'h-8 w-8 rounded-full border border-light-gray object-cover',
              user.subscription && 'ring-2 ring-rich-gold'
            )}
            title="Account settings"
          />
        ) : (
          <div
            className={clsx(
              'h-8 w-8 rounded-full border border-light-gray flex items-center justify-center text-sm font-bold text-white',
              'bg-charcoal', // or use bg-dominant-purple if you want color pop
              user.subscription && 'ring-2 ring-rich-gold'
            )}
            title="Account settings"
          >
            {user.email?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition out-quad duration-150"
        enterFrom="scale-95 opacity-0"
        enterTo="scale-100 opacity-100"
        leave="transition in-out duration-100"
        leaveFrom="scale-100 opacity-100"
        leaveTo="scale-95 opacity-0"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 rounded bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-40">
          <div className="px-4 py-2">
            <p className="text-sm font-medium text-charcoal">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
            <p className="text-xs text-slate-500 mt-1">
              Access:&nbsp;
              {user.subscription
                ? 'Subscription'
                : user.bundle
                  ? `Bundle (${user.bundle})`
                  : user.features?.length > 0
                    ? 'Single Template Access'
                    : 'Limited'}
            </p>
            <p className="text-xs text-slate-500 mt-0.5 italic">
              Role: {user.role || 'Unset'}
            </p>
          </div>

          <Menu.Item>
            {({ active }) => (
              <Link
                to="/dashboard"
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 text-sm',
                  active && 'bg-soft-yellow/40'
                )}
              >
                <HomeIcon className="h-5 w-5" />
                Dashboard
              </Link>
            )}
          </Menu.Item>

          <Menu.Item>
            {({ active }) => (
              <Link
                to="/templates"
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 text-sm',
                  active && 'bg-soft-yellow/40'
                )}
              >
                <RectangleStackIcon className="h-5 w-5" />
                Templates
              </Link>
            )}
          </Menu.Item>

          {user.is_admin && !Capacitor.isNativePlatform() && (
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/admin/blank-cards"
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 text-sm',
                    active && 'bg-soft-yellow/40'
                  )}
                >
                  <RectangleStackIcon className="h-5 w-5" />
                  Blank card export
                </Link>
              )}
            </Menu.Item>
          )}

          <Menu.Item>
            {({ active }) => (
              <Link
                to="/settings"
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 text-sm',
                  active && 'bg-soft-yellow/40'
                )}
              >
                <Cog8ToothIcon className="h-5 w-5" />
                Settings
              </Link>
            )}
          </Menu.Item>

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={logout}
                className={clsx(
                  'w-full flex items-center gap-2 px-4 py-2 text-sm text-left',
                  active && 'bg-soft-yellow/40'
                )}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                Sign out
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}